export class ReactiveState {
  /*
   * @param {Object.<string, *>} initialState
   */
  constructor (initialState = {}) {
    /**
     * @type {Object.<string, any>}
     * @private
     */
    this._internalState = {}

    Object.entries(initialState).forEach(([key, initialValue]) =>
      this._makeKeyReactive(key, initialValue),
    )
  }

  /**
   * This will return whether a key has been set in the state
   *
   * @param {string} key - The key from the state tree we want
   *
   * @returns {boolean}
   */
  has (key) {
    return this._checkIfKeyInState(key, false)
  }

  /**
   * This allows you to add keys to be tracked after initialisation
   *
   * @param {string} key - The key from the state tree we want
   * @param {*} initialValue - The initial value for this key
   */
  add (key, initialValue) {
    this._makeKeyReactive(key, initialValue)
  }

  /**
   * Subscribe to a key to get notified when it's value changes
   *
   * @param {string} key - The key from the state tree we want
   * @param {Function} fnc - The function that will get run when the key's value changes
   */
  subscribe (key, fnc) {
    if (!this._checkIfKeyInState(key)) {
      throw new Error(`${key} doesn't exist so can't be subscribed to.`)
    }

    const privateKey = getPrivateKey(key)

    this._internalState[privateKey].listeners.push(fnc)
  }

  /**
   * Stop getting updates when this key's value changes
   *
   * @param {string} key - The key from the state tree we want
   * @param {Function} fnc - The function that will get run when the key's value changes
   */
  unsubscribe (key, fnc) {
    if (!this._checkIfKeyInState(key)) return
    const privateKey = getPrivateKey(key)

    /* prettier-ignore */
    this._internalState[privateKey].listeners = this._internalState[
      privateKey
    ].listeners.filter((fn) => fn !== fnc)
  }

  /**
   * This will run all of the subscribed functions to a key
   *
   * @param {string} key - The key from the state tree we want
   * @param {*} val - The new value
   * @param {*} [prevVal] - The previous value
   * @private
   */
  _notify (key, val, { prevVal }) {
    if (!this._checkIfKeyInState(key)) return
    const privateKey = getPrivateKey(key)

    this._internalState[privateKey].listeners.forEach((fn) =>
      fn(val, {
        prevVal,
      }),
    )
  }

  /**
   * This is the magic that allows us to subscribe to state changes.
   *
   * This will take the key and add a getter and setter to it so we can notify all subscriptions
   * when the value gets set
   *
   * @param {string} key - The key from the state tree we want
   * @param {*} initialValue - The initial value for this key
   * @private
   *
   * @returns {Object} The internal state
   */
  _makeKeyReactive (key, initialValue) {
    const _this = this
    const privateKey = getPrivateKey(key)

    this._internalState[privateKey] = {
      value: initialValue,
      listeners: [],
    }

    Object.defineProperty(this, key, {
      get () {
        return _this._internalState[privateKey].value
      },
      set (val) {
        const prevVal = _this._internalState[privateKey].value

        _this._internalState[privateKey].value = val

        _this._notify(key, val, {
          prevVal,
        })
      },
    })

    return this._internalState
  }

  /**
   * Before we doing anything regarding keys internally we want to see if it exists
   *
   * @param {string} key - The key from the state tree we want
   * @param {boolean} [throwError=true] - Whether to throw an error if the key doesn't exist
   * @private
   */
  _checkIfKeyInState (key, throwError = true) {
    const privateKey = getPrivateKey(key)
    const keyInState = privateKey in this._internalState

    if (keyInState === false && throwError) {
      throw new Error(`${key} doesn't exist in ObservableState`)
    }

    return keyInState
  }
}

/**
 * Internally we use a different key to the public key, this is just a helper to get that
 *
 * @param {string} key - The key from the state tree we want
 *
 * @returns {string} The key prefixed with an `_`
 */
function getPrivateKey (key) {
  return `_${key}`
}
